import { darken, lighten, rgba } from 'polished';
import { variant } from 'styled-system';
import * as Colors from '../../constants/Colors';

export const ButtonDisplayArray = [
    'transparent',
    'link',
    'link-white',
    'light',
    'green',
    'primary',
    'primary-inverted',
    'primary-inverted-no-border',
    'secondary',
    'cancel',
    'blue',
    'blue-inverted',
    'option',
    'option-selected',
    'member-primary',
    'member-primary-inverted',
    'member-secondary',
    'member-secondary-inverted',
    'member-tertiary',
    'link-member',
] as const;
type ButtonDisplayTuple = typeof ButtonDisplayArray;
export type ButtonDisplay = ButtonDisplayTuple[number];

type ButtonDisplayProps = {
    [key: string]: string | number | unknown;
};

export const displayVariant = variant<ButtonDisplayProps, ButtonDisplay>({
    prop: 'display',
    variants: {
        primary: {
            color: Colors.white,
            border: `solid 2px ${Colors.hsBlue}`,
            backgroundColor: Colors.hsBlue,
            fontWeight: 600,
            '&:hover': {
                color: Colors.white,
            },
            '&:hover:not([disabled])': {
                backgroundColor: lighten(0.1, Colors.hsBlue),
            },
            '&:focus': {
                boxShadow: `0 0 3px 3px ${rgba(Colors.hsBlue, 0.25)}`,
            },
        },
        'primary-inverted': {
            color: Colors.hsBlue,
            border: `solid 2px ${Colors.hsBlue}`,
            backgroundColor: Colors.white,
            fontWeight: 600,
            '&:hover': {
                color: Colors.hsBlue,
            },
            '&:hover:not([disabled])': {
                backgroundColor: Colors.hsBlue5,
            },
            '&:focus': {
                boxShadow: `0 0 3px 3px ${rgba(Colors.hsBlue, 0.25)}`,
            },
        },
        'primary-inverted-no-border': {
            color: Colors.hsBlue,
            border: `solid 2px ${Colors.white}`,
            backgroundColor: Colors.white,
            fontWeight: 600,
            '&:hover': {
                color: Colors.hsBlue,
            },
            '&:hover:not([disabled])': { backgroundColor: Colors.white },
            '&:focus': { boxShadow: `0 0 3px 3px ${rgba(Colors.hsBlue, 0.25)}` },
        },
        secondary: {
            color: Colors.hsBlue,
            border: `solid 2px ${Colors.hsBlue}`,
            backgroundColor: Colors.purple20,
            fontWeight: 600,
            '&:hover': {
                color: Colors.hsBlue,
            },
            '&:hover:not([disabled])': {
                backgroundColor: darken(0.1, Colors.purple20),
            },
            '&:focus': {
                boxShadow: `0 0 3px 2px ${rgba(Colors.hsBlue, 0.5)}`,
            },
        },
        cancel: {
            color: Colors.pink,
            border: `solid 2px ${Colors.pink}`,
            backgroundColor: Colors.white,
            fontWeight: 600,
            '&:hover': {
                color: Colors.pink,
            },
            '&:hover:not([disabled])': {
                backgroundColor: Colors.pink5,
            },
            '&:focus': {
                boxShadow: `0 0 3px 3px ${rgba(Colors.pink, 0.25)}`,
            },
        },
        light: {
            color: Colors.hsBlue,
            border: `solid 2px ${Colors.purple50}`,
            backgroundColor: Colors.white,
            fontWeight: 600,
            '&:hover': {
                color: Colors.hsBlue,
            },
            '&:hover:not([disabled])': {
                backgroundColor: Colors.purple10,
            },
            '&:focus': {
                boxShadow: `0 0 3px 3px ${rgba(Colors.purple, 0.25)}`,
            },
        },
        green: {
            color: Colors.darkGreen,
            border: `solid 2px #7bc9a5`,
            backgroundColor: Colors.green20,
            fontWeight: 600,
            '&:hover': {
                color: Colors.darkGreen,
            },
            '&:hover:not([disabled])': {
                backgroundColor: lighten(0.1, Colors.green20),
            },
            '&:focus': {
                boxShadow: `0 0 3px 3px ${rgba(Colors.green, 0.25)}`,
            },
        },
        'member-primary': {
            color: Colors.Neutral000,
            backgroundColor: Colors.MemberGreen500,
            fontWeight: 600,
            border: `solid 2px transparent`,
            '&:hover:not([disabled]), &:focus-visible': {
                backgroundColor: Colors.MemberGreen600,
            },
            '&:active': {
                backgroundColor: Colors.MemberGreen700,
            },
            '&:disabled': {
                backgroundColor: Colors.MemberGreen100,
                color: Colors.Neutral300,
            },
        },
        'member-primary-inverted': {
            color: Colors.MemberGreen500,
            backgroundColor: Colors.Neutral000,
            fontWeight: 600,
            border: `solid 2px transparent`,
            '&:hover:not([disabled]), &:focus-visible': {
                backgroundColor: Colors.MemberGreen100,
            },
            '&:active': {
                backgroundColor: Colors.MemberGreen200,
            },
            '&:disabled': {
                backgroundColor: Colors.MemberGreen100,
                color: Colors.Neutral300,
            },
        },
        'member-secondary': {
            color: Colors.MemberGreen700,
            backgroundColor: 'transparent',
            border: `2px solid ${Colors.MemberGreen200}`,
            fontWeight: 600,
            '&:hover:not([disabled]), &:focus-visible': {
                color: Colors.MemberGreen600,
                borderColor: Colors.MemberGreen600,
            },
            '&:active': {
                borderColor: Colors.MemberGreen700,
                color: Colors.MemberGreen700,
            },
            '&:disabled': {
                borderColor: Colors.MemberGreen100,
                color: Colors.MemberGreen100,
            },
        },
        'member-secondary-inverted': {
            color: Colors.Neutral000,
            backgroundColor: 'transparent',
            border: `2px solid ${Colors.MemberGreen200}`,
            fontWeight: 600,
            '&:hover:not([disabled]), &:focus-visible': {
                color: Colors.MemberGreen200,
                borderColor: Colors.MemberGreen200,
            },
            '&:active': {
                borderColor: Colors.MemberGreen400,
                color: Colors.MemberGreen400,
            },
            '&:disabled': {
                borderColor: Colors.MemberGreen500,
                color: Colors.MemberGreen500,
            },
        },
        'member-tertiary': {
            color: Colors.MemberGreen500,
            backgroundColor: 'transparent',
            border: `2px solid transparent`,
            fontWeight: 600,
            '&:hover:not([disabled]), &:focus-visible': {
                color: Colors.MemberGreen600,
            },
            '&:active': {
                color: Colors.MemberGreen700,
            },
            '&:disabled': {
                color: Colors.Neutral300,
            },
        },
        link: {
            color: Colors.hsBlue,
            border: `solid 2px transparent`,
            backgroundColor: 'transparent',
            fontWeight: '400',
            '.button-content': {
                textDecoration: 'underline',
            },
            '&:hover': {
                color: Colors.hsBlue,
            },
            '&:hover:not([disabled]), &:focus': {
                color: lighten(0.1, Colors.hsBlue),
                '.button-content': {
                    textDecoration: 'none',
                },
            },
        },
        'link-white': {
            color: Colors.white,
            border: `solid 2px transparent`,
            backgroundColor: 'transparent',
            fontWeight: '400',
            '.button-content': {
                textDecoration: 'underline',
            },
            '&:hover': {
                color: Colors.white,
            },
            '&:hover:not([disabled]), &:focus': {
                '.button-content': {
                    textDecoration: 'none',
                },
            },
        },
        'link-member': {
            color: Colors.MemberGreen500,
            border: `solid 2px transparent`,
            backgroundColor: 'transparent',
            fontWeight: '400',
            '.button-content': {
                textDecoration: 'underline',
            },
            '&:hover:not([disabled]), &:focus': {
                '.button-content': {
                    textDecoration: 'none',
                    color: Colors.MemberGreen400,
                },
            },
        },
        blue: {
            color: Colors.black,
            border: `solid 2px ${Colors.blue}`,
            backgroundColor: Colors.blue,
            fontWeight: 600,
            '&:hover': {
                color: Colors.black,
            },
            '&:hover:not([disabled])': {
                backgroundColor: lighten(0.1, Colors.blue),
            },
            '&:focus': {
                boxShadow: `0 0 3px 3px ${rgba(Colors.blue, 0.25)}`,
            },
        },
        'blue-inverted': {
            color: Colors.black,
            border: `solid 2px ${Colors.blue}`,
            backgroundColor: Colors.white,
            fontWeight: 600,
            '&:hover': {
                color: Colors.black,
            },
            '&:hover:not([disabled])': {
                backgroundColor: Colors.blue5,
            },
            '&:focus': {
                boxShadow: `0 0 3px 3px ${rgba(Colors.blue, 0.25)}`,
            },
        },
        transparent: {
            color: 'inherit',
            border: 'none',
            backgroundColor: 'transparent',
            fontWeight: 600,
            '&:hover': {
                color: 'inherit',
            },
            '&:hover:not([disabled])': {
                color: 'inherit',
                backgroundColor: 'transparent',
            },
        },
        option: {
            color: Colors.black,
            border: `solid 2px ${Colors.grey}`,
            backgroundColor: Colors.white,
            fontWeight: 600,
            '&:hover': {
                color: Colors.black,
                textDecoration: 'none',
            },
            '&:hover:not([disabled])': {
                backgroundColor: Colors.veryLightGrey,
            },
            '&:focus': {
                boxShadow: `0 0 3px 3px ${rgba(Colors.black, 0.25)}`,
            },
        },
        'option-selected': {
            color: Colors.black,
            border: `solid 2px ${Colors.blue50}`,
            backgroundColor: Colors.blue10,
            fontWeight: 600,
            '&:hover': {
                color: Colors.black,
                textDecoration: 'none',
            },
            '&:hover:not([disabled])': {
                backgroundColor: Colors.blue20,
            },
            '&:focus': {
                boxShadow: `0 0 3px 3px ${rgba(Colors.black, 0.25)}`,
            },
        },
    },
});
